// src/components/legal/LegalModal.tsx
// Reusable modal shell used by all four legal modals.
// — Glassmorphism card, focus trap, overlay-click & Escape to dismiss.
// — Content can be plain React children OR dangerouslySetInnerHTML HTML string.
// — Firestore HTML override is handled by each child modal.

import React, { useCallback, useEffect, useRef } from 'react';
import { useThemePalette }    from '../../hooks/useThemePalette';
import { useThemeStore }      from '../../stores/useThemeStore';
import { useLegalModalStore } from '../../stores/useLegalModalStore';

// ── Keyframe injection ────────────────────────────────────────────────────────
(function injectLegalModalStyles() {
  if (typeof document === 'undefined') return;
  const id = 'esp-legal-modal-kf';
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @keyframes legalOverlayIn  { from { opacity: 0; } to { opacity: 1; } }
    @keyframes legalOverlayOut { from { opacity: 1; } to { opacity: 0; } }
    @keyframes legalCardIn  {
      from { opacity: 0; transform: translateY(24px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0)    scale(1);    }
    }
    @keyframes legalCardOut {
      from { opacity: 1; transform: translateY(0)    scale(1);    }
      to   { opacity: 0; transform: translateY(12px) scale(0.98); }
    }
    .esp-legal-content h2 {
      font-size:      1.15rem;
      font-weight:    800;
      letter-spacing: -0.02em;
      margin:         28px 0 10px;
    }
    .esp-legal-content h2:first-child { margin-top: 0; }
    .esp-legal-content h3 {
      font-size:      0.95rem;
      font-weight:    700;
      margin:         20px 0 8px;
    }
    .esp-legal-content p {
      font-size:   0.88rem;
      line-height: 1.72;
      margin:      0 0 12px;
    }
    .esp-legal-content ul, .esp-legal-content ol {
      padding-left: 20px;
      margin:       0 0 12px;
    }
    .esp-legal-content li {
      font-size:   0.88rem;
      line-height: 1.72;
      margin:      0 0 6px;
    }
    .esp-legal-content a {
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .esp-legal-content a:hover { opacity: 0.75; }
    .esp-legal-content strong { font-weight: 700; }
    .esp-legal-content hr {
      border: none;
      height: 1px;
      margin: 24px 0;
    }
    .esp-legal-close-btn {
      transition: background 0.18s ease, transform 0.18s ease !important;
    }
    .esp-legal-close-btn:hover {
      transform: scale(1.08) !important;
    }
    @media (prefers-reduced-motion: reduce) {
      .esp-legal-close-btn { transition: none !important; }
    }
  `;
  document.head.appendChild(s);
})();

// ── Focus trap helper ─────────────────────────────────────────────────────────
function trapFocus(container: HTMLElement, e: KeyboardEvent) {
  const focusable = container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
  );
  if (!focusable.length) return;
  const first = focusable[0];
  const last  = focusable[focusable.length - 1];
  if (e.shiftKey) {
    if (document.activeElement === first) { e.preventDefault(); last.focus(); }
  } else {
    if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface LegalModalProps {
  isOpen:        boolean;
  title:         string;
  lastUpdated?:  string;
  htmlContent?:  string;            // dangerouslySetInnerHTML path
  children?:     React.ReactNode;   // JSX path
}

export function LegalModal({ isOpen, title, lastUpdated, htmlContent, children }: LegalModalProps) {
  const C      = useThemePalette();
  const isDark = useThemeStore((s) => s.theme) === 'dark';
  const close  = useLegalModalStore((s) => s.close);

  const overlayRef  = useRef<HTMLDivElement>(null);
  const cardRef     = useRef<HTMLDivElement>(null);
  const closeRef    = useRef<HTMLButtonElement>(null);

  // Focus close button on open
  useEffect(() => {
    if (isOpen) {
      const id = requestAnimationFrame(() => closeRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [isOpen]);

  // Escape key + focus trap
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'Tab' && cardRef.current) trapFocus(cardRef.current, e);
  }, [close]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const contentColor = isDark ? C.text : C.text;
  const mutedColor   = isDark ? C.muted : C.muted;

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => { if (e.target === overlayRef.current) close(); }}
      style={{
        position:        'fixed',
        inset:           0,
        zIndex:          9000,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         '16px',
        background:      isDark
          ? 'rgba(4, 7, 14, 0.80)'
          : 'rgba(15, 23, 42, 0.45)',
        backdropFilter:  'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        animation:       'legalOverlayIn 0.22s ease both',
      }}
    >
      <div
        ref={cardRef}
        style={{
          position:     'relative',
          width:        '100%',
          maxWidth:     '680px',
          maxHeight:    '88vh',
          display:      'flex',
          flexDirection: 'column',
          borderRadius: '20px',
          background:   isDark
            ? 'rgba(13, 18, 32, 0.92)'
            : 'rgba(255, 255, 255, 0.96)',
          border:       `1px solid ${C.borderH}`,
          boxShadow:    isDark
            ? '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04) inset'
            : '0 32px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04) inset',
          backdropFilter:       'blur(24px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
          animation:    'legalCardIn 0.28s cubic-bezier(0.34, 1.56, 0.64, 1) both',
          overflow:     'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display:      'flex',
          alignItems:   'flex-start',
          justifyContent: 'space-between',
          padding:      '24px 28px 20px',
          borderBottom: `1px solid ${C.border}`,
          flexShrink:   0,
        }}>
          <div>
            <h2 style={{
              margin:        0,
              fontSize:      '1.2rem',
              fontWeight:    800,
              color:         contentColor,
              letterSpacing: '-0.025em',
              lineHeight:    1.2,
            }}>
              {title}
            </h2>
            {lastUpdated && (
              <p style={{ margin: '5px 0 0', fontSize: '0.76rem', color: mutedColor, fontWeight: 500 }}>
                Last updated: {lastUpdated}
              </p>
            )}
          </div>

          {/* Close button */}
          <button
            ref={closeRef}
            onClick={close}
            aria-label="Close modal"
            className="esp-legal-close-btn"
            style={{
              flexShrink:     0,
              marginLeft:     '12px',
              width:          '34px',
              height:         '34px',
              borderRadius:   '10px',
              border:         `1px solid ${C.border}`,
              background:     C.card,
              cursor:         'pointer',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              color:          mutedColor,
              fontFamily:     'inherit',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div
          style={{
            flex:       1,
            overflowY:  'auto',
            padding:    '28px',
            color:      contentColor,
            lineHeight: 1.7,
          }}
        >
          {/* Accent top line */}
          <div style={{
            width:        '40px',
            height:       '3px',
            borderRadius: '99px',
            background:   `linear-gradient(90deg, ${C.accent}, ${C.accent2})`,
            marginBottom: '22px',
          }} />

          <div
            className="esp-legal-content"
            style={{ '--esp-link-color': C.accent, '--esp-hr-color': C.border } as React.CSSProperties}
          >
            {/* Inject per-theme CSS vars into content */}
            <style>{`
              .esp-legal-content { color: ${contentColor}; }
              .esp-legal-content h2 { color: ${contentColor}; }
              .esp-legal-content h3 { color: ${contentColor}; }
              .esp-legal-content p  { color: ${mutedColor};   }
              .esp-legal-content li { color: ${mutedColor};   }
              .esp-legal-content a  { color: ${C.accent};     }
              .esp-legal-content hr { background: ${C.border}; }
            `}</style>

            {htmlContent
              ? <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
              : children
            }
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding:    '16px 28px',
          borderTop:  `1px solid ${C.border}`,
          display:    'flex',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}>
          <button
            onClick={close}
            style={{
              padding:      '9px 22px',
              borderRadius: '10px',
              background:   `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
              color:        '#fff',
              fontWeight:   700,
              fontSize:     '0.88rem',
              border:       'none',
              cursor:       'pointer',
              fontFamily:   'inherit',
              letterSpacing: '0.01em',
              transition:   'opacity 0.18s ease, transform 0.18s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1';    e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}